import { useMemo, useState } from "react";
import { FaSort, FaCalendarAlt, FaChevronDown } from "react-icons/fa";
import { MONTHS, formatNumber, rowTotal } from "./BudgetGrid";
import BudgetProgressBar from "./BudgetProgressBar";

// No se muestra "0.00" en la grilla de ejecucion, igual que los inputs de asignacion.
const displayNumber = (n) => {
    const formatted = formatNumber(n);
    return formatted === "0.00" ? "" : formatted;
};

export default function BudgetExecutionGrid({ categories, budgetValues, consumptionValues }) {
    const [sortAsc, setSortAsc] = useState(true);
    // "ALL" = total del año, o la key de un mes puntual (month01..month12).
    const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth()].key);

    // El consumo puede venir de meses ya transcurridos, a diferencia de la grilla de edicion
    // (que solo permite editar del mes actual en adelante). Aqui se considera el año completo.
    const monthsToSum = useMemo(
        () => (selectedMonth === "ALL" ? MONTHS : MONTHS.filter((m) => m.key === selectedMonth)),
        [selectedMonth]
    );

    const sortedCategories = useMemo(() => {
        const list = [...categories];
        list.sort((a, b) => {
            const cmp = (a.categoryName || "").localeCompare(b.categoryName || "");
            return sortAsc ? cmp : -cmp;
        });
        return list;
    }, [categories, sortAsc]);

    const rows = useMemo(
        () =>
            sortedCategories.map((c) => {
                const asignado = rowTotal(budgetValues[c.categoryId], monthsToSum);
                const consumo = rowTotal(consumptionValues[c.categoryId], monthsToSum);
                const saldo = asignado - consumo;
                const pct = asignado > 0 ? (consumo / asignado) * 100 : 0;
                return { ...c, asignado, consumo, saldo, pct };
            }),
        [sortedCategories, budgetValues, consumptionValues, monthsToSum]
    );

    const totals = rows.reduce(
        (acc, r) => ({
            asignado: acc.asignado + r.asignado,
            consumo: acc.consumo + r.consumo,
            saldo: acc.saldo + r.saldo,
        }),
        { asignado: 0, consumo: 0, saldo: 0 }
    );
    const totalPct = totals.asignado > 0 ? (totals.consumo / totals.asignado) * 100 : 0;

    const stickyCol = "sticky left-0 z-20 w-[240px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]";

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-200 bg-gray-50/60">
                <FaCalendarAlt className="text-gray-400 flex-shrink-0" size={14} />
                <label className="text-sm font-medium text-gray-700">Mes</label>
                <div className="relative">
                    <select
                        className="appearance-none border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm font-medium text-gray-800 hover:border-gray-400 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 focus:outline-none transition-colors"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                        <option value="ALL">Total año</option>
                        {MONTHS.map((m) => (
                            <option key={m.key} value={m.key}>
                                {m.label}
                            </option>
                        ))}
                    </select>
                    <FaChevronDown
                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        size={10}
                    />
                </div>

                <div className="ml-auto flex items-center gap-4 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-green-500" /> &lt;70%
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-amber-500" /> 70-100%
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-red-500" /> &gt;100%
                    </span>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="text-gray-500 text-left text-xs uppercase tracking-wide border-b border-gray-200">
                            <th
                                className={`px-5 py-3 font-semibold whitespace-nowrap cursor-pointer select-none bg-white ${stickyCol}`}
                                onClick={() => setSortAsc((s) => !s)}
                            >
                                <span className="inline-flex items-center gap-1.5">
                                    Categoría <FaSort className="text-gray-300" size={10} />
                                </span>
                            </th>
                            <th className="px-4 py-3 font-semibold text-right whitespace-nowrap min-w-[110px]">
                                Asignado
                            </th>
                            <th className="px-4 py-3 font-semibold text-right whitespace-nowrap min-w-[110px]">
                                Consumo
                            </th>
                            <th className="px-4 py-3 font-semibold text-right whitespace-nowrap min-w-[110px]">
                                Saldo
                            </th>
                            <th className="px-5 py-3 font-semibold text-left whitespace-nowrap min-w-[210px]">
                                Ejecución
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {rows.map((r) => (
                            <tr key={r.categoryId} className="bg-white hover:bg-gray-50/80 transition-colors">
                                <td className={`px-5 py-3 whitespace-nowrap font-medium text-gray-800 bg-white ${stickyCol}`}>
                                    {r.categoryName}
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap tabular-nums text-gray-600">
                                    {displayNumber(r.asignado)}
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap tabular-nums text-gray-600">
                                    {displayNumber(r.consumo)}
                                </td>
                                <td
                                    className={`px-4 py-3 text-right whitespace-nowrap tabular-nums font-medium ${
                                        r.saldo < 0 ? "text-red-600" : "text-gray-800"
                                    }`}
                                >
                                    {displayNumber(r.saldo)}
                                </td>
                                <td className="px-5 py-3">
                                    <BudgetProgressBar asignado={r.asignado} pct={r.pct} consumo={r.consumo} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-brand-primary/5 font-semibold text-gray-900 border-t-2 border-brand-primary/20">
                            <td className={`px-5 py-3.5 whitespace-nowrap bg-brand-primary/5 ${stickyCol}`}>
                                {rows.length} Registros
                            </td>
                            <td className="px-4 py-3.5 text-right whitespace-nowrap tabular-nums">
                                {displayNumber(totals.asignado)}
                            </td>
                            <td className="px-4 py-3.5 text-right whitespace-nowrap tabular-nums">
                                {displayNumber(totals.consumo)}
                            </td>
                            <td
                                className={`px-4 py-3.5 text-right whitespace-nowrap tabular-nums ${
                                    totals.saldo < 0 ? "text-red-600" : "text-gray-900"
                                }`}
                            >
                                {displayNumber(totals.saldo)}
                            </td>
                            <td className="px-5 py-3.5">
                                <BudgetProgressBar asignado={totals.asignado} pct={totalPct} />
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}
